import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MetaDefinition } from '@angular/platform-browser';
import jsPDF from 'jspdf';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class AppToolsGstInvoiceBuilderService {
  private gstinPattern =/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  private hsnSacPattern = /^(?:\d{4}|\d{6}|\d{8})$/;
  private panCardPattern = /[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  inforidaLogoUrl = 'https://inforida.com/assets/images/logo.png';

  terms = `1. Full payment is due upon receipt; late payments may incur a 1.5% monthly fee.
2. This invoice is GST-compliant; please review for accuracy and report discrepancies within 7 days.
3. Returns within 7 days in original condition are eligible for a refund; processing takes up to 15 days.`;

  note = 'This is a computer-generated invoice and does not require a physical signature.';

  states: string[] = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry',
  ];

  constructor(private fb: FormBuilder) {}

  getStates() {
    return [
      'Andhra Pradesh',
      'Arunachal Pradesh',
      'Assam',
      'Bihar',
      'Chhattisgarh',
      'Goa',
      'Gujarat',
      'Haryana',
      'Himachal Pradesh',
      'Jharkhand',
      'Karnataka',
      'Kerala',
      'Madhya Pradesh',
      'Maharashtra',
      'Manipur',
      'Meghalaya',
      'Mizoram',
      'Nagaland',
      'Odisha',
      'Punjab',
      'Rajasthan',
      'Sikkim',
      'Tamil Nadu',
      'Telangana',
      'Tripura',
      'Uttar Pradesh',
      'Uttarakhand',
      'West Bengal',
      'Andaman and Nicobar Islands',
      'Chandigarh',
      'Dadra and Nagar Haveli and Daman and Diu',
      'Delhi',
      'Jammu and Kashmir',
      'Ladakh',
      'Lakshadweep',
      'Puducherry',
    ];
  }

  getRates() {
    return [0, 5, 12, 18, 28, 0.25, 3];
  }

  getGstRatesMap() {
    return {
      // 0% GST - Essential goods, unprocessed agricultural products
      '01': { gstRate: 0, type: 'Live Animals' },
      '02': { gstRate: 0, type: 'Meat' },
      '03': { gstRate: 0, type: 'Fish' },
      '04': { gstRate: 0, type: 'Dairy Products' },
      '05': { gstRate: 0, type: 'Other Animal Products' },
      '06': { gstRate: 0, type: 'Live Plants' },
      '07': { gstRate: 0, type: 'Vegetables' },
      '08': { gstRate: 0, type: 'Fruits and Nuts' },
      '09': { gstRate: 0, type: 'Coffee, Tea, and Spices' },
      '1001': { gstRate: 0, type: 'Cereals (e.g., Wheat)' },
      '1002': { gstRate: 0, type: 'Rye' },
      '1003': { gstRate: 0, type: 'Barley' },
      '1004': { gstRate: 0, type: 'Oats' },
      '1005': { gstRate: 0, type: 'Corn' },
      '1006': { gstRate: 0, type: 'Rice' },
      '1007': { gstRate: 0, type: 'Grain Sorghum' },
      '1008': { gstRate: 0, type: 'Millet and other Cereals' },
      '7102': { gstRate: 0.25, type: 'Uncut Diamonds' },
      '7103': { gstRate: 0.25, type: 'Precious and Semi-precious Stones' },

      // 3% GST - Precious metals like gold, silver, platinum
      '71': { gstRate: 3, type: 'Gold, Silver, Platinum, and Jewelry' },
      '7106': { gstRate: 3, type: 'Silver (including semi-manufactured)' },
      '7110': { gstRate: 3, type: 'Platinum and other Platinum Group Metals' },
      '7108': { gstRate: 3, type: 'Gold Jewelry' },

      // 5% GST - Basic essentials, fresh produce
      '0302': { gstRate: 5, type: 'Fish (excluding ornamental)' },
      '0401': { gstRate: 5, type: 'Milk' },
      '0801': { gstRate: 5, type: 'Coconuts, fresh or dried' },
      '0810': { gstRate: 5, type: 'Other Fruits' },
      '89': { gstRate: 5, type: 'Ships and Boats' },
      '4901': { gstRate: 5, type: 'Books' },
      '4902': { gstRate: 5, type: 'Newspapers' },
      '4907': { gstRate: 5, type: 'Maps and Globes' },

      // 12% GST - Processed foods, medical items, select goods
      '1905': { gstRate: 12, type: 'Bread, Pastries, and Cakes' },
      '22': { gstRate: 12, type: 'Non-Alcoholic Beverages' },
      '3004': { gstRate: 12, type: 'Medicines' },
      '3301': { gstRate: 12, type: 'Essential Oils' },
      '3307': { gstRate: 12, type: 'Personal Care Items' },
      '44': { gstRate: 12, type: 'Wood Articles' },
      '84': { gstRate: 12, type: 'Machinery (Non-Electrical)' },
      '9404': { gstRate: 12, type: 'Mattresses' },
      '8432': { gstRate: 12, type: 'Agricultural Machinery' },

      // 18% GST - Standard goods and services
      '1517': { gstRate: 18, type: 'Edible Oils' },
      '1704': { gstRate: 18, type: 'Sugar Confectionery' },
      '2103': { gstRate: 18, type: 'Sauces, Ketchups' },
      '2202': { gstRate: 18, type: 'Soft Drinks' },
      '2710': { gstRate: 18, type: 'Petroleum Oils' },
      '33': { gstRate: 18, type: 'Cosmetics and Toiletries' },
      '38': { gstRate: 18, type: 'Chemicals' },
      '39': { gstRate: 18, type: 'Plastics' },
      '8418': { gstRate: 18, type: 'Refrigerators' },
      '85': { gstRate: 18, type: 'Electrical Machinery' },
      '9004': { gstRate: 18, type: 'Spectacle Lenses' },
      '9405': { gstRate: 18, type: 'Lamps and Lightings' },

      // 28% GST - Luxury items, cars, cigarettes
      '2402': { gstRate: 28, type: 'Cigarettes' },
      '2523': { gstRate: 28, type: 'Cement' },
      '3305': { gstRate: 28, type: 'Perfumes and Deodorants' },
      '4202': { gstRate: 28, type: 'Suitcases, Briefcases' },
      '6404': { gstRate: 28, type: 'Footwear (above ₹1000)' },
      '6802': { gstRate: 28, type: 'Marble and Granite' },
      '69': { gstRate: 28, type: 'Ceramic Products' },
      '8703': { gstRate: 28, type: 'Passenger Vehicles' },
      '8711': { gstRate: 28, type: 'Motorcycles' },
      '9403': { gstRate: 28, type: 'Furniture' },
      '9504': { gstRate: 28, type: 'Video Games and Consoles' },

      // Additional Entries and Special Cases
      '2106': { gstRate: 18, type: 'Food Supplements' },
      '8507': { gstRate: 28, type: 'Electrical Storage Batteries' },
      '8511': { gstRate: 28, type: 'Ignition or starting equipment' },
      '9703': { gstRate: 12, type: 'Antiques' },
      '9401': { gstRate: 12, type: 'Seats and Seat Parts' },
      '4802': { gstRate: 12, type: 'Paper and Paperboard' },
      '3926': { gstRate: 18, type: 'Other Articles of Plastics' },
      '8528': { gstRate: 18, type: 'Monitors and Projectors' },
      '8708': { gstRate: 28, type: 'Parts and Accessories of Vehicles' },
      '3304': { gstRate: 18, type: 'Beauty and Skincare' },
      '3006': { gstRate: 5, type: 'Vaccines' },
      '3215': { gstRate: 18, type: 'Inks for Writing, Printing' },
      '1516': { gstRate: 12, type: 'Animal and Vegetable Oils' },
      '8509': { gstRate: 18, type: 'Household Appliances' },
    };
  }

  getSEOTags(): MetaDefinition[] {
    return [
      {
        name: 'description',
        content:
        "Create GST-compliant invoices instantly with Inforida's AI Powered GST Invoice Generator. Ideal for Indian businesses of all sizes, ensuring accuracy, compliance, and ease of use for seamless invoicing across diverse sectors."
      },
      {
        "name": "keywords",
        "content": "GST Invoice Generator, online GST invoice generator, create online GST invoice, GST Tax Invoice Generator, Indian business GST invoices, GST invoice for Indian business, GST Invoice for small business, All-in-one GST invoice solution, AI-powered GST invoice generator, online GST billing, GST-compliant invoices for India, easy GST billing for SMEs, GST invoicing software, automated GST compliance, GST invoice creation tool, digital tax invoice, GST billing software, tax invoice generator, GST software for businesses, GST invoicing for startups, GST tool for professionals, instant GST invoice, GST for service providers"
      },
      {
        property: 'og:title',
        content: 'AI-Powered GST Invoice Generator | Inforida',
      },
      {
        property: 'og:description',
        content:
          "Quickly create GST-compliant invoices with Inforida's GST Invoice Generator, tailored for Indian business needs.",
      },
      {
        property: 'og:image',
        content:
          'https://cdn.dribbble.com/users/4908/screenshots/2787171/media/a54da5ba89330cf4804a4d7b21012067.gif',
      },
      {
        property: 'og:url',
        content:
          'https://cdn.dribbble.com/users/4908/screenshots/2787171/media/a54da5ba89330cf4804a4d7b21012067.gif',
      },
      { name: 'twitter:card', content: 'summary_large_image' },
      {
        name: 'twitter:title',
        content: 'GST Invoice Generator for Indian Businesses | Inforida',
      },
      {
        name: 'twitter:description',
        content:
          "Create GST-compliant invoices easily with Inforida's generator, optimised for Indian businesses.",
      },
      {
        name: 'twitter:image',
        content:
          'https://cdn.dribbble.com/users/4908/screenshots/2787171/media/a54da5ba89330cf4804a4d7b21012067.gif',
      },
    ];
  }

  getJsonLD() {
    return {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'GST Invoice Generator',
      description:
        'A GST-compliant invoice generator for Indian businesses, powered by Inforida.',
      operatingSystem: 'Web',
      applicationCategory: 'Business Application',
      url: 'https://inforida.com/tools/gst-invoice',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'INR',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Inforida Technologies',
      },
    };
  }

  initializeInvoiceForm(): FormGroup {
    return this.fb.group({
      invoiceNumber: [ 'INV-10000' + Math.floor(Math.random() * 9), Validators.required],
      invoiceDate: [new Date(), Validators.required],
      supplierName: ['', Validators.required],
      supplierAddress: [''],
      supplierState: ['', Validators.required],
      supplierGstin: [ '', [ Validators.pattern(this.gstinPattern)]],
      supplierPan: [ '', [ Validators.pattern(this.panCardPattern)]],
      termsAndConditions: [this.terms],
      customerName: ['', Validators.required],
      customerAddress: [''],
      customerState: ['', Validators.required],
      customerGstin: ['',[Validators.pattern(this.gstinPattern)]],
      customerPan: [ '', [ Validators.pattern(this.panCardPattern)]],
      items: this.fb.array([]),
      totalAmount: [0],
      cgst: [0],
      sgst: [0],
      igst: [0],
    });
  }

  initializeItems() {
    return this.fb.group({
      description: ['', Validators.required],
      hsnSac: [
        '', [Validators.required, Validators.pattern(this.hsnSacPattern)],
      ],
      price: [0, Validators.required],
      quantity: [1, Validators.required],
      gst: [null, Validators.required],
      gstTypeHint: [''],
    });
  }

  calculateTaxBreakdown(items: any[], isInterstate: boolean) {
    let totalTaxable = 0;
    let cgst = 0;
    let sgst = 0;
    let igst = 0;

    items.forEach((item) => {
      const itemTotal = item.price * item.quantity;
      const gstAmount = (itemTotal * item.gst) / 100;

      totalTaxable += itemTotal;

      // Calculate CGST/SGST for intrastate, IGST for interstate
      if (isInterstate) {
        igst += gstAmount;
      } else {
        cgst += gstAmount / 2;
        sgst += gstAmount / 2;
      }
    });

    const totalAmount = totalTaxable + cgst + sgst + igst;

    // Return the tax breakdown as an object
    return {
      totalTaxable: parseFloat(totalTaxable.toFixed(2)),
      cgst: parseFloat(cgst.toFixed(2)),
      sgst: parseFloat(sgst.toFixed(2)),
      igst: parseFloat(igst.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2)),
    };
  }

  generatePDF(
    invoiceData: any,
    uploadedLogoUrl: string,
    isInterstate: boolean
  ): void {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 16;
    const lineHeight = 8;
    const yPosition = margin;
    const headerBgColor = '#007bff';
    const textColor = '#333';

    this.addStyledInvoiceContent(
      doc,
      yPosition,
      pageWidth,
      margin,
      lineHeight,
      textColor,
      headerBgColor,
      invoiceData,
      uploadedLogoUrl,
      isInterstate
    );
  }


  

  addStyledInvoiceContent(
    doc: jsPDF,
    yPosition: number,
    pageWidth: number,
    margin: number,
    lineHeight: number,
    textColor: string,
    headerBgColor: string,
    invoiceData: any,
    uploadedLogoUrl: string,
    isInterstate: boolean
  ) {
    // Invoice Header Box at the Top of the Receipt
    const boxHeight = lineHeight * 1.4; // Define height of the box
    const boxMarginTop = 0; // Define top margin for the box
    const boxPadding = 0; // Padding within the box

    // Draw the header box
    doc.setDrawColor('#FFFFFF');
    doc.setFillColor('#FFFFFF'); // Light background color for the box
    doc.roundedRect(
      margin,
      boxMarginTop,
      pageWidth - 2 * margin,
      boxHeight,
      0,
      0,
      'FD'
    );

    // Center the text vertically within the box
    const textYPosition = boxMarginTop + boxHeight / 2 + lineHeight;

    doc.setFontSize(10);
    doc.setTextColor(headerBgColor);

    // Invoice Number (Left-Aligned within the Box)
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice No : ', margin + boxPadding, textYPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(invoiceData.invoiceNumber, margin + 21, textYPosition); // Adjusted for spacing

    // Invoice Date (Right-Aligned within the Box)
    doc.setFont('helvetica', 'bold');
    doc.text('Date : ', pageWidth - margin - 34, textYPosition); // Align date label closer to the right
    doc.setFont('helvetica', 'normal');
    const formattedDate = moment(invoiceData.invoiceDate).format(
      'Do MMM, YYYY'
    );
    doc.text(formattedDate, pageWidth - margin, textYPosition, {
      align: 'right',
    });

    // Move yPosition below the box for the next section
    yPosition = boxMarginTop + boxHeight + lineHeight;
    yPosition += lineHeight;

    let leftMargin = margin;

    // Supplier Logo if Available
    if (uploadedLogoUrl) {
      const supplierLogo = new Image();
      supplierLogo.src = uploadedLogoUrl;
      const aspectRatio = supplierLogo.width / supplierLogo.height;
      const fixedWidth = Math.min(supplierLogo.width, 24);
      const dynamicHeight = Math.min(fixedWidth * aspectRatio, 24);
      doc.addImage(
        supplierLogo,
        'PNG',
        margin,
        yPosition - 6,
        dynamicHeight,
        fixedWidth
      ); // Adjust logo size and position
      leftMargin = margin + 30; // Adjust text left margin if logo is present
    }

    // Supplier Name
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#000000'); // A darker shade for a professional look
    doc.text(invoiceData.supplierName.toUpperCase(), leftMargin, yPosition);
    yPosition += lineHeight;

    // Address + State
    doc.setTextColor('#484848');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const address = `${invoiceData.supplierAddress.toUpperCase()} , ${invoiceData.supplierState.toUpperCase()}`;
    doc.text(address, leftMargin, yPosition);
    yPosition += lineHeight;

    // GSTIN and PAN on the same line
    doc.setFont('helvetica', 'bold');
    doc.text('GSTIN : ', leftMargin, yPosition);
    doc.setFont('helvetica', 'italic');
    doc.text(invoiceData.supplierGstin.toUpperCase(), leftMargin + 14, yPosition);

    
    if (invoiceData.supplierPan) {
      doc.setFont('helvetica', 'bold');
      doc.text('PAN : ', leftMargin + 80, yPosition); // Adjusted position for PAN
      doc.setFont('helvetica', 'italic');
      doc.text(invoiceData.supplierPan.toUpperCase(), leftMargin + 90, yPosition);
      yPosition += lineHeight;
    }

    // Customer Information Section
    const customerBoxHeight = lineHeight * 5.5; // Increased height for proper spacing
    const customerBoxPadding = 4; // Padding within the box

    // Customer Information Content
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(headerBgColor); // Dark blue for header text
    doc.setDrawColor(headerBgColor); // Dark blue border color
    doc.setFillColor('#e7f3ff'); // Very light blue background color
    doc.roundedRect(
      margin,
      yPosition,
      pageWidth - 2 * margin,
      customerBoxHeight,
      0,
      0,
      'FD'
    );
    doc.text('Bill To', margin + customerBoxPadding, yPosition + lineHeight);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(textColor);
    yPosition += lineHeight * 2;

    // Name
    doc.setFont('helvetica', 'bold');
    doc.text(
      invoiceData.customerName.toUpperCase(),
      margin + customerBoxPadding,
      yPosition
    );
    yPosition += lineHeight;

    // Address and State
    doc.setTextColor('#484848');
    doc.setFont('helvetica', 'normal');
    const customerAddress = `${invoiceData.customerAddress.toUpperCase()}, ${invoiceData.customerState.toUpperCase()}`;
    doc.text(customerAddress, margin + customerBoxPadding, yPosition);
    yPosition += lineHeight; // Extra spacing for next section

    // GSTIN and PAN on the same line
    doc.setFont('helvetica', 'bold');
    doc.text('GSTIN : ', margin + customerBoxPadding, yPosition);
    doc.setFont('helvetica', 'italic');
    doc.text(invoiceData.customerGstin.toUpperCase(), margin + 18, yPosition);

    
    if (invoiceData.customerPan) {
      doc.setFont('helvetica', 'bold');
      doc.text('PAN : ', margin + customerBoxPadding + 80, yPosition); // Adjusted position for PAN
      doc.setFont('helvetica', 'italic');
      doc.text(invoiceData.customerPan.toUpperCase(), margin + 94, yPosition);
      yPosition += lineHeight;
    }

    // Item Table Header
    if (invoiceData.items.length > 0) {
      // Set up header row for items table
      doc.setTextColor(headerBgColor);
      doc.setDrawColor(headerBgColor);
      doc.setFillColor('#e7f3ff'); // Light blue for header background
      doc.rect(
        margin,
        yPosition,
        pageWidth - 2 * margin,
        lineHeight * 1.2,
        'FD'
      );
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);

      // Table headers with borders
      doc.text('ITEM', margin + 5, yPosition + lineHeight - 2);
      doc.text('QUANTITY', margin + 60, yPosition + lineHeight - 2, {
        align: 'center',
      });
      doc.text('PRICE', margin + 95, yPosition + lineHeight - 2, {
        align: 'center',
      });
      doc.text('HSN/SAC', margin + 130, yPosition + lineHeight - 2, {
        align: 'center',
      });
      doc.text('TOTAL', pageWidth - margin - 5, yPosition + lineHeight - 2, {
        align: 'right',
      });

      yPosition += lineHeight * 1.2;

      // Initialize totals
      let totalAmount = 0;
      let totalQuantity = 0;
      const totalItems = invoiceData.items.length;

      let cgst = 0;
      let sgst = 0;
      let igst = 0;

      // Render each item row and calculate GST for each item
      invoiceData.items.forEach((item: any, index: number) => {
        const itemTotal = item.price * item.quantity;
        const itemGstAmount = (itemTotal * item.gst) / 100;
        totalAmount += itemTotal;
        totalQuantity += item.quantity;

        // Accumulate GST based on interstate or intrastate
        if (isInterstate) {
          igst += itemGstAmount;
        } else {
          cgst += itemGstAmount / 2;
          sgst += itemGstAmount / 2;
        }

        // Alternate row background color with borders for better readability
        doc.setFontSize(9);
        doc.setFillColor(index % 2 === 0 ? '#ffffff' : '#f0f7ff');
        doc.setDrawColor(headerBgColor); // Dark blue border color
        doc.rect(
          margin,
          yPosition,
          pageWidth - 2 * margin,
          lineHeight * 1.2,
          'FD'
        );
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(textColor);

        doc.text(item.description, margin + 5, yPosition + lineHeight - 2);
        doc.text(
          item.quantity.toString(),
          margin + 60,
          yPosition + lineHeight - 2,
          { align: 'center' }
        );
        doc.text(
          `Rs. ${this.formatNumberWithCommas(item.price.toFixed(2))}`,
          margin + 95,
          yPosition + lineHeight - 2,
          { align: 'center' }
        );
        doc.text(item.hsnSac, margin + 130, yPosition + lineHeight - 2, {
          align: 'center',
        });
        doc.text(
          `Rs. ${this.formatNumberWithCommas(itemTotal.toFixed(2))}`,
          pageWidth - margin - 5,
          yPosition + lineHeight - 2,
          { align: 'right' }
        );

        yPosition += lineHeight * 1.2;
      });

      // Total row for items with a contrasting color and no border radius
      doc.setTextColor(headerBgColor);
      doc.setFillColor('#D3D3D3'); // Gray background for better contrast
      doc.setDrawColor(headerBgColor);
      doc.rect(
        margin,
        yPosition,
        pageWidth - 2 * margin,
        lineHeight * 1.2,
        'FD'
      );
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(textColor);

      doc.text('Total Items:', margin + 5, yPosition + lineHeight - 2);
      doc.text(totalItems.toString(), margin + 24, yPosition + lineHeight - 2);
      doc.text('Total Quantity:', margin + 90, yPosition + lineHeight - 2);
      doc.text(
        totalQuantity.toString(),
        margin + 114,
        yPosition + lineHeight - 2
      );
      doc.text(
        `Rs. ${this.formatNumberWithCommas(totalAmount.toFixed(2))}`,
        pageWidth - margin - 5,
        yPosition + lineHeight - 2,
        { align: 'right' }
      );

      yPosition += lineHeight * 2;

      // GST Breakdown Section with half-width alignment and yellow background
      const gstSectionWidth = (pageWidth - 2 * margin) / 2;
      doc.setFontSize(10);
      doc.setTextColor('#000000'); // Black text for GST breakdown
      doc.setFillColor('#FFD700'); // Yellow background
      doc.setDrawColor(headerBgColor); // Dark blue border
      doc.rect(pageWidth / 2, yPosition, gstSectionWidth, lineHeight * 4, 'FD');

      doc.text('GST Breakdown', pageWidth / 2 + 5, yPosition + lineHeight - 2);
      doc.setFont('helvetica', 'normal');
      yPosition += lineHeight;

      // Display only non-zero GST values with bold keys
      if (cgst > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('CGST:', pageWidth / 2 + 5, yPosition + lineHeight - 2);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Rs. ${this.formatNumberWithCommas(cgst.toFixed(2))}`,
          pageWidth / 2 + 25,
          yPosition + lineHeight - 2
        );
        yPosition += lineHeight;
      }

      if (sgst > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('SGST:', pageWidth / 2 + 5, yPosition + lineHeight - 2);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Rs. ${this.formatNumberWithCommas(sgst.toFixed(2))}`,
          pageWidth / 2 + 25,
          yPosition + lineHeight - 2
        );
        yPosition += lineHeight;
      }

      if (igst > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('IGST:', pageWidth / 2 + 5, yPosition + lineHeight - 2);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Rs. ${this.formatNumberWithCommas(igst.toFixed(2))}`,
          pageWidth / 2 + 25,
          yPosition + lineHeight - 2
        );
        yPosition += lineHeight;
      }

      // Grand Total Amount Display with bold styling and highlighted background
      const grandTotal = totalAmount + cgst + sgst + igst;
      yPosition += 2 * lineHeight;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor('#FFFFFF');
      doc.setDrawColor(headerBgColor); // Dark blue border
      doc.setFillColor(headerBgColor); // Highlighted color for grand total
      doc.rect(
        margin,
        yPosition,
        pageWidth - 2 * margin,
        lineHeight * 1.5,
        'FD'
      );
      doc.text('Total Amount:', margin + 5, yPosition + lineHeight);
      doc.text(
        `Rs. ${this.formatNumberWithCommas(grandTotal.toFixed(2))}`,
        pageWidth - margin - 5,
        yPosition + lineHeight,
        { align: 'right' }
      );

      yPosition += lineHeight * 2;
    }

    // Add Terms and Conditions Section
    const termsAndConditions = invoiceData.termsAndConditions;
    if (termsAndConditions) {
      yPosition += lineHeight;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(headerBgColor);
      doc.text('Terms and Conditions:', margin, yPosition);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(textColor);
      const splitText = doc.splitTextToSize(
        termsAndConditions,
        pageWidth - 2 * margin
      );
      yPosition += lineHeight;
      splitText.forEach((line: string) => {
        doc.text(line, margin, yPosition);
        yPosition += lineHeight;
      });
    }

    // Footer with Powered by Inforida and Center-Aligned Logo

    yPosition += lineHeight;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor('#808080');
    doc.text(`NOTE: ${this.note}`, margin, yPosition);

    yPosition = doc.internal.pageSize.getHeight() - 25;
    doc.setFontSize(10);
    doc.setTextColor(textColor);
    doc.setFont('helvetica', 'normal');
    doc.text('Powered by Inforida', pageWidth / 2, yPosition, {
      align: 'center',
    });
    const inforidaLogo = new Image();
    inforidaLogo.src = this.inforidaLogoUrl;
    inforidaLogo.onload = () => {
      const maxWidth = 16; // Set the maximum width for the logo
      const maxHeight = 10; // Set the maximum height for the logo

      // Calculate aspect ratio and dimensions
      const aspectRatio = inforidaLogo.width / inforidaLogo.height;
      let logoWidth = maxWidth;
      let logoHeight = maxHeight;

      if (aspectRatio > 1) {
        // Landscape orientation
        logoHeight = maxWidth / aspectRatio;
      } else {
        // Portrait orientation or square
        logoWidth = maxHeight * aspectRatio;
      }
      doc.addImage(
        inforidaLogo,
        'PNG',
        pageWidth / 2 - 7.5,
        yPosition + 4,
        logoWidth,
        logoHeight
      );
      doc.save(`Invoice_${invoiceData.invoiceNumber}.pdf`);
    };
  }

  formatNumberWithCommas(value: string | number): string {
    const number = typeof value === 'string' ? parseFloat(value) : value;
    return number.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}
